<template>
  <div>
    <!-- Success Dialog -->
    <v-dialog
      v-model="successBox"
      max-width="500"
      persistent
    >
      <v-card class="success-dialog-card">
        <v-card-text align="center" class="pt-8 pb-4 pl-2 pr-2">
          <div class="success-icon-wrapper">
            <v-icon size="64" color="success">mdi-check-circle</v-icon>
          </div>
          <h3 class="success-title">Payment Successful!</h3>
          <p class="success-subtitle">
            Your tokens have been added to your wallet. Please refresh the page to see the changes.
          </p>
        </v-card-text>
        <v-card-actions class="px-6 pb-6">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="successBox = false"
            class="dialog-btn"
          >
            Close
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <div class="pa-0 ma-0">
      <div class="body-overall">
        <v-row style="padding-top:64px!important"></v-row>
        <div :class="getGextPanel" class="pt-6" style="margin:0px!important;">
          <div class="fonts-white container pl-0 pr-1">
            <div class="overlay-grey-web">
              <!-- Header Section -->
              <div class="payment-header">
                <div class="header-content">
                  <v-icon class="header-icon">mdi-cash-multiple</v-icon>
                  <h1 class="header-title">Top Up Tokens</h1>
                </div>
                <p class="header-subtitle">
                  Buy tokens securely using Stripe, PayPal or Metamask. You buy from us, we buy from users.
                </p>
                <div class="header-actions">
                  <v-btn
                    variant="outlined"
                    prepend-icon="mdi-refresh"
                    @click="refreshWallet"
                    class="action-btn"
                  >
                    Refresh Wallet
                  </v-btn>
                  <v-btn
                    variant="outlined"
                    prepend-icon="mdi-history"
                    class="action-btn"
                    :to="`/acc/wallet`"
                  >
                    Payment History
                  </v-btn>
                </div>
              </div>

              <v-divider class="my-4" style="opacity:0.3;"></v-divider>

              <!-- Info Alert -->
              <v-alert
                type="info"
                variant="outlined"
                class="payment-info-alert mb-4"
                icon="mdi-information"
              >
                <div class="alert-content">
                  <div class="alert-title">Secure Payment</div>
                  <div class="alert-text">
                    All transactions are encrypted and secure. Choose your preferred payment method and follow the steps to complete your purchase.
                  </div>
                </div>
              </v-alert>

              <!-- Payment Steps -->
              <div v-if="user && cryptoDashboardData" class="payment-section">
                <v-card class="payment-card">
                  <!-- Step 1: Choose Tokens -->
                  <div v-show="cryptoPayModel > 0" class="payment-step">
                    <h2 class="step-title">
                      <v-icon class="mr-2">mdi-numeric-1-circle</v-icon>
                      Choose Number of Tokens
                    </h2>

                    <!-- Error Alerts -->
                    <div v-if="!user.active" class="step-alert">
                      <v-icon left>mdi-account-off</v-icon>
                      <span>Account disabled!</span>
                      <v-btn
                        variant="text"
                        size="small"
                        class="ml-2"
                        :to="`/acc/settings`"
                      >
                        Reactivate Account
                      </v-btn>
                    </div>
                    <div v-if="!cryptoDashboardData.release_amount_left > 0" class="step-alert">
                      <v-icon left>mdi-cancel</v-icon>
                      <span>Tokens sold out!</span>
                    </div>
                    <div v-if="!cryptoDashboardData.data_live > 0" class="step-alert">
                      <v-icon left>mdi-alert</v-icon>
                      <span>Token data offline!</span>
                    </div>

                    <!-- Token Selection -->
                    <div class="token-selection">
                      <div class="token-info">
                        <div class="token-amount">
                          <span class="label">Tokens:</span>
                          <span class="value">{{ cryptoSlider.val }}</span>
                        </div>
                        <div class="token-price">
                          <span class="label">Total:</span>
                          <span class="value">{{ (cryptoSlider.val * cryptoDashboardData.release_price).toFixed(2) }} USD</span>
                        </div>
                      </div>

                      <div class="slider-wrapper">
                        <v-slider
                          :disabled="(!cryptoDashboardData.release_amount_left>0) || !cryptoDashboardData.data_live>0 || !user.active || cryptoPayModel > 3"
                          v-model="cryptoSlider.val"
                          :thumb-color="cryptoSlider.color"
                          track-color="rgba(255,255,255,0.2)"
                          color="white"
                          thumb-label="always"
                          :min="computeMinSlider()"
                          :max="computeMaxSlider()"
                        ></v-slider>
                      </div>
                    </div>

                    <div class="step-actions">
                      <v-btn
                        :disabled="!cryptoSlider.val>0 || (!cryptoDashboardData.release_amount_left>0) || !cryptoDashboardData.data_live>0 || !user.active"
                        variant="outlined"
                        block
                        @click="cryptoPayModel = 2"
                        class="action-btn"
                      >
                        Continue
                      </v-btn>
                    </div>
                  </div>

                  <!-- Step 2: Accept Terms -->
                  <v-expand-transition>
                    <div v-show="cryptoPayModel > 1" class="payment-step">
                      <h2 class="step-title">
                        <v-icon class="mr-2">mdi-numeric-2-circle</v-icon>
                        Accept Terms & Privacy Policy
                      </h2>

                      <div class="terms-checkbox">
                        <v-checkbox
                          v-model="checkboxSignup"
                          :rules="[v => !!v || 'You must agree to continue!']"
                          required
                          hide-details
                          color="white"
                          :disabled="cryptoPayModel > 2"
                        >
                          <template v-slot:label>
                            <span class="checkbox-label">
                              I accept the
                              <v-btn text plain variant="text" class="policy-link" @click.stop="handleClickMenu('Terms')">
                                Terms of use
                              </v-btn>
                              &amp;
                              <v-btn text plain variant="text" class="policy-link" @click.stop="handleClickMenu('Privacy')">
                                Privacy policy
                              </v-btn>
                            </span>
                          </template>
                        </v-checkbox>
                      </div>

                      <div class="step-actions">
                        <v-btn
                          @click="cryptoPayModel = 3"
                          :disabled="!checkboxSignup"
                          variant="outlined"
                          block
                          class="action-btn mb-2"
                        >
                          Continue
                        </v-btn>
                        <v-btn
                          @click="cryptoPayModel = 1"
                          variant="outlined"
                          block
                          class="action-btn"
                        >
                          Back
                        </v-btn>
                      </div>
                    </div>
                  </v-expand-transition>

                  <!-- Step 3: Payment Method -->
                  <v-expand-transition>
                    <div v-show="cryptoPayModel > 2" class="payment-step">
                      <h2 class="step-title">
                        <v-icon class="mr-2">mdi-numeric-3-circle</v-icon>
                        Summary & Payment Method
                      </h2>

                      <div class="payment-summary">
                        <div class="summary-row">
                          <span class="summary-label">Tokens:</span>
                          <span class="summary-value">{{ cryptoSlider.val }}</span>
                        </div>
                        <div class="summary-row total">
                          <span class="summary-label">Total Amount:</span>
                          <span class="summary-value">{{ (cryptoSlider.val * cryptoDashboardData.release_price).toFixed(2) }} USD</span>
                        </div>
                      </div>

                      <h3 class="subsection-title">
                        <v-icon class="mr-2" size="20">mdi-credit-card-outline</v-icon>
                        Choose Payment Method
                      </h3>

                      <div class="payment-methods">
                        <div
                          v-for="image in imagesPayment"
                          :key="image.id"
                          class="payment-method"
                          :class="{ selected: selectedPaymentMethod == image.id }"
                          @click="onSelectImage(image)"
                        >
                          <v-img
                            :src="image.src"
                            :alt="image.alt"
                            max-height="50px"
                            contain
                          ></v-img>
                        </div>
                      </div>

                      <div v-if="initializeError" class="error-message">
                        <v-icon left size="20">mdi-alert-circle</v-icon>
                        Error occurred, please try again later.
                      </div>

                      <div
                        v-if="selectedPaymentMethod == 3 && !(isMetamaskActive && $store.getters.getMetamaskWallet && $store.getters.getMetamaskWallet.web3)"
                        class="error-message"
                      >
                        <v-icon left size="20">mdi-alert-circle</v-icon>
                        You must activate Metamask before use.
                      </div>

                      <div class="step-actions">
                        <v-btn
                          @click="initPayment()"
                          :disabled="!(selectedPaymentMethod == 1 || selectedPaymentMethod == 2 || (selectedPaymentMethod == 3 && isMetamaskActive && $store.getters.getMetamaskWallet && $store.getters.getMetamaskWallet.web3))"
                          variant="outlined"
                          block
                          class="action-btn mb-2"
                        >
                          Continue to Payment
                        </v-btn>
                        <v-btn
                          @click="cryptoPayModel = 2"
                          variant="outlined"
                          block
                          class="action-btn"
                        >
                          Back
                        </v-btn>
                      </div>
                    </div>
                  </v-expand-transition>

                  <!-- Step 4: Payment Gateway -->
                  <v-expand-transition>
                    <div v-show="cryptoPayModel > 3" class="payment-step">
                      <h2 class="step-title">
                        <v-icon class="mr-2">mdi-numeric-4-circle</v-icon>
                        Complete Payment
                      </h2>

                      <!-- Stripe -->
                      <div v-if="selectedPaymentMethod == 1" class="payment-gateway">
                        <div class="gateway-logo">
                          <v-img :src="require('@/assets/logo/stripe.png')" contain max-width="150px"></v-img>
                        </div>

                        <div class="stripe-form">
                          <stripe-element-payment
                            v-if="clientSecretStripe !== ''"
                            ref="paymentRefStripe"
                            pk="pk_live_51KUbvpGTdhGW2BcRwvTDsLGCv6lUlQNoikZqhRnu7R9yCvuaV87Gk12ST82MfiM6qhR1g8u8vqfQ4wcIayL2QQfR00OXRpesrz"
                            :elements-options="elementsOptions"
                            :confirm-params="confirmParams"
                          />
                        </div>

                        <div v-if="initializeError2" class="error-message">
                          <v-icon left size="20">mdi-alert-circle</v-icon>
                          Error occurred, please try again.
                        </div>

                        <div class="step-actions">
                          <v-btn
                            @click="submitPaymentStripe()"
                            :disabled="isexecuteStripe"
                            :loading="isexecuteStripe"
                            variant="outlined"
                            block
                            class="action-btn mb-2"
                          >
                            Pay Now
                          </v-btn>
                          <v-btn
                            @click="clientSecretStripe = ''; elementsOptions.clientSecret = ''; cryptoPayModel = 3; $refs.paymentRefStripe.clear()"
                            variant="outlined"
                            block
                            class="action-btn"
                          >
                            Back
                          </v-btn>
                        </div>
                      </div>

                      <!-- PayPal -->
                      <div v-if="selectedPaymentMethod == 2" class="payment-gateway">
                        <div class="gateway-logo">
                          <v-img :src="require('@/assets/logo/paypal.webp')" contain max-width="150px"></v-img>
                        </div>

                        <div id="paypal-button-container" ref="paypal-button-container" class="paypal-container"></div>

                        <div class="step-actions">
                          <v-btn
                            @click="clientOrderID = ''; cryptoPayModel = 3; changeDivPP();"
                            variant="outlined"
                            block
                            class="action-btn"
                          >
                            Back
                          </v-btn>
                        </div>
                      </div>

                      <!-- Metamask -->
                      <div v-if="selectedPaymentMethod == 3" class="payment-gateway">
                        <div class="gateway-logo">
                          <v-img :src="require('@/assets/icons/metamask.png')" contain max-width="150px"></v-img>
                        </div>

                        <div class="metamask-info">
                          <div class="info-row">
                            <span class="info-label">Tokens:</span>
                            <span class="info-value">{{ cryptoSlider.val }}</span>
                          </div>
                          <div class="info-row">
                            <span class="info-label">Your Balance:</span>
                            <span class="info-value">{{ yourWalletBalanceEth }} ETH</span>
                          </div>
                          <div class="info-row total">
                            <span class="info-label">Amount to Pay:</span>
                            <span class="info-value">{{ ethTokenPrice }} ETH</span>
                          </div>
                        </div>

                        <div v-if="errorMetamask2 && isMetamaskActive" class="error-message">
                          <v-icon left size="20">mdi-alert-circle</v-icon>
                          {{ errorMetamask2 }}
                        </div>

                        <div class="step-actions">
                          <v-btn
                            @click="submitMetamaskPaymentToken()"
                            :disabled="isexecuteMetamask || (yourWalletBalanceEth < ethTokenPrice)"
                            :loading="isexecuteMetamask"
                            variant="outlined"
                            block
                            class="action-btn mb-2"
                          >
                            Pay with Metamask
                          </v-btn>
                          <v-btn
                            @click="intentMetamaskID = ''; cryptoPayModel = 3;"
                            variant="outlined"
                            block
                            class="action-btn"
                          >
                            Back
                          </v-btn>
                        </div>
                      </div>
                    </div>
                  </v-expand-transition>
                </v-card>
              </div>

              <!-- Empty State (if no crypto data) -->
              <div v-else-if="user && !cryptoDashboardData" class="payment-empty-state">
                <div class="empty-icon-wrapper">
                  <v-icon size="64" color="grey">mdi-alert-circle-outline</v-icon>
                </div>
                <h3 class="empty-title">Token Data Unavailable</h3>
                <p class="empty-subtitle">
                  Unable to load token purchase information. Please try again later.
                </p>
                <div class="empty-actions">
                  <v-btn
                    variant="outlined"
                    prepend-icon="mdi-refresh"
                    @click="refreshWallet"
                    class="empty-action-btn"
                  >
                    Retry
                  </v-btn>
                </div>
              </div>

            </div>
          </div>
        </div>
        <v-row
          :style="(isMobileXs || isMobilePlayer3) ? 'padding-top:170px!important':'padding-top:123px!important'"
        ></v-row>
      </div>
    </div>
  </div>
</template>

<script>
// ... (cały istniejący script bez zmian)
import { useMobile } from '../composables/useMobile.js'
import { useMobilePlayer3 } from '../composables/useMobilePlayer3.js'
import { useMobileXs } from '../composables/useMobileXs.js'
import { toast } from "vue3-toastify";
import "vue3-toastify/dist/index.css";
import Web3 from 'web3'
import { StripeElementPayment } from '@vue-stripe/vue-stripe';

export default {
  setup() {
    const isMobile = useMobile()
    const isMobileXs = useMobileXs()
    const isMobilePlayer3 = useMobilePlayer3();

    const notify = (message) => {
      toast(message, {
        theme: "dark",
        type: "success",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 1500,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    const notifyError = (message) => {
      toast(message, {
        theme: "dark",
        type: "error",
        position: "bottom-center",
        closeOnClick: false,
        pauseOnHover: false,
        autoClose: 3000,
        transition: "flip",
        dangerouslyHTMLString: true,
      });
    };

    return { isMobile, isMobileXs, notify, notifyError, isMobilePlayer3 }
  },
  name: 'Payment',

  components: {
    StripeElementPayment
  },
  data: () => ({
    navbar: false,
    user: null,
    successBox: false,

    cryptoDashboardData: null,
    cryptoUserData: null,
    cryptoPayModel: 1,

      cryptoSlider: { label: 'Tokens', val: 50, color: 'white' },
      checkboxSignup: '',
      selectedPaymentMethod: 0,
      initializeError: false,
      initializeError2: false,
      clientSecretStripe: '',
      clientOrderID: '',
      executeStripe: false,
      executeMetamask: false,

      elementsOptions: {
        appearance: {}, // appearance options
        loader: 'always'
      },

      confirmParams: {},
      ethTokenPrice: 0,
      yourWalletBalanceEth: 0,
      intentMetamaskID: '',
      errorMetamask2: '',

      imagesPayment: [
        {
          id: '1',
          src: require('@/assets/logo/stripe.png'),
          alt: 'Stripe'
        }, {
          id: '2',
          src: require('@/assets/logo/paypal.webp'),
          alt: 'PayPal'
        }, {
          id: '3',
          src: require('@/assets/icons/metamask.png'),
          alt: 'Metamask'
        }
      ],
  }),
  mounted() {   
    window.addEventListener('resize', this.checkScreenSize);
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.checkScreenSize);
  },
  beforeUnmount() {

  },
  destroyed() {
  },
  watch: {
    '$store.state.user': function() {
          this.user = this.$store.getters.getUserData;
      },    
  },
  computed: {
    isfullScreen(){
      if(this.isMobile && this.isMobileXs){
        return false;
      } else {
        return true;
      }
    },
    isLogged() {
          return this.$store.getters.getUserData != null
      },
    isDesktop() {
        if (!this.navbar){
          return true;
        } else {
          return false;
        }
      },
    getGextPanel() {
      if(this.navbar){
        if (this.isMobileXs) {
          return 'textPanelMobileXs'
        } else {
          return 'textPanelMobile';
        }
      } else return 'textPanel';
    },
    isMetamaskActive() {
            return this.$store.getters.getIsMetamaskWalletActive;
      },
    isexecuteMetamask() {
        return this.executeMetamask;
      },
    isexecuteStripe() {
        return this.executeStripe;
      },

  },  
  created() {
    if (this.$store.getters.getUserData != null) {
      this.user = this.$store.getters.getUserData;    

      if(window.innerWidth > 768){
        this.navbar = false;
      } else {
        this.navbar = true;
      }

      this.emitter.on('cryptoSuccessModal', () => {
            this.successBox = true;
      })

      this.$store.dispatch('getCryptoDashboardData').then( response => {        
      this.cryptoDashboardData = this.$store.getters.getCryptoDashboardData
      }).catch( error => {
        if (!error.response) {
          this.notifyError("Error occured.. Please try again later.");
        }
      })

      if (this.isLogged){
        this.$store.dispatch('getCryptoUserData', { token: this.user.token}).then( response => {
          this.cryptoUserData = this.$store.getters.getCryptoUserData          
        }).catch( error => {
          if (!error.response) {
            this.notifyError("Error occured.. Please try again later.");
          }
        })
      }

      if (this.$route.matched.some(({ name }) => name === 'main_success')) {
        this.emitter.emit('cryptoSuccessModal');
      }

      this.initializeError = false;
      this.initializeError2 = false;
      this.clientSecretStripe = '';
      this.clientOrderID = '';
      this.intentMetamaskID = '';
      this.executeStripe = false;
      this.ethTokenPrice = 0;
      this.yourWalletBalanceEth = 0;
      this.executeMetamask = false;
      this.cryptoPayModel = 1;
      this.checkboxSignup = '';
      this.confirmParams = { return_url: 'https://login.muzible.com/acc/payment/success' }
    }
  }, 
  methods: {
    onSelectImage(obj) {
      if (this.cryptoPayModel > 3){
        return;
      }
      this.selectedPaymentMethod = obj.id
    },
    submitMetamaskPaymentToken() {
              this.executeMetamask = false;
              this.errorMetamask2 = '';
              if (this.$store.getters.getMetamaskWallet && this.$store.getters.getMetamaskWallet.web3){
                this.executeMetamask = true;
                this.emitter.emit('transactionInProgress');    
                this.$store.getters.getMetamaskWallet.web3.eth.sendTransaction( {from: this.$store.getters.getMetamaskWallet.metaMaskAddress, to: this.$store.getters.getLifeMasterAddress, value: parseInt(this.ethTokenPrice * (10 ** 18)) })
                .on('confirmation', (confirmationNumber, receipt) => {
                   if (confirmationNumber === 2){
                      this.$store.getters.getMetamaskWallet.web3.eth.getTransaction(receipt.transactionHash, (err2, response3) => {
                          if(!err2){
                              var data2 = {
                                transactionHash: receipt.transactionHash,
                              }
                              this.emitter.emit('transactionData', data2);
                              this.emitter.emit('refreshMetamaskBalance');
                              this.sleeper2().then( rxs => {
                              this.$store.dispatch('executeMetamaskPaymentTokenIntent', { token: this.user.token, trx: receipt.transactionHash, 
                                                                                          from: this.$store.getters.getMetamaskWallet.metaMaskAddress, 
                                                                                          to: this.$store.getters.getLifeMasterAddress, intentID: this.intentMetamaskID })
                                  .then( response => {
                                      this.refreshWallet();                    
                                      this.emitter.emit('transactionInProgressEnd');  
                                      this.executeMetamask = false;      
                                      this.cryptoPayModel = 5;            
                                      this.cryptoBox = false;      
                                  })
                                  .catch( error => {
                                    this.errorMetamask2 = error.message;
                                    this.emitter.emit('transactionInProgressEndError', this.errorMetamask2);
                                    this.emitter.emit('transactionInProgressEnd'); 
                                    this.executeMetamask = false;
                                  })   
                              })
                          } else {
                              var data = {
                                transactionHash: receipt.transactionHash,
                              }
                              this.emitter.emit('transactionData', data);
                              this.emitter.emit('refreshMetamaskBalance');
                              this.sleeper2().then( rxs => {
                              this.$store.dispatch('executeMetamaskPaymentTokenIntent', { token: this.user.token, trx: receipt.transactionHash, 
                                                                                          from: this.$store.getters.getMetamaskWallet.metaMaskAddress, 
                                                                                          to: this.$store.getters.getLifeMasterAddress, intentID: this.intentMetamaskID })
                                  .then( response => {
                                      this.refreshWallet();                    
                                      this.emitter.emit('transactionInProgressEnd');  
                                      this.executeMetamask = false;     
                                      this.cryptoPayModel = 5;   
                                      this.cryptoBox = false;                 
                                  })
                                  .catch( error => {
                                    this.errorMetamask2 = error.message;
                                    this.emitter.emit('transactionInProgressEndError', this.errorMetamask2);
                                    this.emitter.emit('transactionInProgressEnd'); 
                                    this.executeMetamask = false;
                                  })   
                              })
                          }   
                      });   
                   }
                })           
                .on('error', (error) => {  
                      this.errorMetamask2 = error.message;
                      this.emitter.emit('transactionInProgressEndError', this.errorMetamask2);
                      this.emitter.emit('transactionInProgressEnd'); 
                      this.executeMetamask = false;

                })
              }
      },
      refreshWallet() {
            this.$store.dispatch('getCryptoDashboardData').then( response => {        
              this.cryptoDashboardData = this.$store.getters.getCryptoDashboardData
              }).catch( error => {
                if (!error.response) {
                  this.notifyError("Error occured.. Please try again later.");
                }
              })

            if (this.isLogged){
                this.$store.dispatch('getCryptoUserData', { token: this.user.token}).then( response => {
                  this.cryptoUserData = this.$store.getters.getCryptoUserData          
                }).catch( error => {
                  if (!error.response) {
                    this.notifyError("Error occured.. Please try again later.");
                  }
                })
            }
      },
    computeMaxSlider(){
      if (this.cryptoDashboardData.release_amount_left > 5000){
        return 5000;
      } else if (this.cryptoDashboardData.release_amount_left <= 5000){
        return this.cryptoDashboardData.release_amount_left;
      } else {
        return 0;
      }

    },
    computeMinSlider(){
      if (this.cryptoDashboardData.release_amount_left > 50){
        return 50;
      } else if (this.cryptoDashboardData.release_amount_left <= 50){
        return this.cryptoDashboardData.release_amount_left;
      } else {
        return 0;
      }
    },
    changeDivPP(){
          var element = document.getElementById('paypal-button-container');
          element.innerHTML = '';
    },
    renderPaypal(ref, id, emailPassed, tokensPassed, amountPassed){
          window.paypal.Buttons({
              createOrder: function (data, actions) {
                           return id;
              },              
              onApprove: function (data, actions) {
                           return ref.$store.dispatch('executePaypalIntentToken', {email: emailPassed, 
                                                                                    tokens: tokensPassed, 
                                                                                    orderID: data.orderID, 
                                                                                    amount: amountPassed}
                                                      ).then( response => {
                                                              actions.redirect('https://login.muzible.com/acc/payment/success');
                                                      }).catch( error => {
                                                              ref.initializeError = true;
                                                      });                          
              },
          }).render("#paypal-button-container");
    },
    initPayment(){
      this.initializeError = false;
      this.initializeError2 = false;
      this.clientSecretStripe = '';
      this.clientOrderID = '';
      this.intentMetamaskID = '';
      this.executeStripe = false;
      this.ethTokenPrice = 0;
      this.yourWalletBalanceEth = 0;
      this.executeMetamask = false;

      if (this.selectedPaymentMethod == 1){
          // Stripe flow
          this.$store.dispatch('createStripeIntentToken', {email: this.user.email, 
                                                           tokens: parseInt(this.cryptoSlider.val), 
                                                           price:  this.cryptoDashboardData.release_price, 
                                                           amount: Math.round(parseFloat((parseInt(this.cryptoSlider.val) * parseFloat(this.cryptoDashboardData.release_price)) * parseInt(100)))}
            ).then( response => {
              this.clientSecretStripe = response.data.client_secret;
              this.elementsOptions.clientSecret = this.clientSecretStripe;
              this.cryptoPayModel = 4;
            }).catch( error => {
                if (!error.response){
                    this.notifyError("Error occured.. Please try again later.");
                } else {
                    this.initializeError = true;
                }
          });       
      } else if (this.selectedPaymentMethod == 2){
        this.$store.dispatch('createPaypalIntentToken', {email: this.user.email, 
                                                         tokens: parseInt(this.cryptoSlider.val), 
                                                         price:  this.cryptoDashboardData.release_price, 
                                                         amount: parseFloat((parseInt(this.cryptoSlider.val) * parseFloat(this.cryptoDashboardData.release_price))).toFixed(2)}
              ).then( response => {                                                 
                this.clientOrderID = response.data.orderID;
                this.cryptoPayModel = 4;
                this.renderPaypal(this, this.clientOrderID, this.user.email, parseInt(this.cryptoSlider.val), parseFloat((parseInt(this.cryptoSlider.val) * parseFloat(this.cryptoDashboardData.release_price))).toFixed(2));
              }).catch( error => {
                        if (!error.response){
                            this.notifyError("Error occured.. Please try again later.");
                        } else {
                            this.initializeError = true;
                        }
              });      
      } else if (this.selectedPaymentMethod == 3){
        if (this.$store.getters.getMetamaskWallet && this.$store.getters.getMetamaskWallet.web3){
          this.GetETHExchangeRate().then( res => {
            this.ethTokenPrice = parseFloat((parseInt(this.cryptoSlider.val) * parseFloat(this.cryptoDashboardData.release_price))).toFixed(2) * res;
            this.$store.getters.getMetamaskWallet.web3.eth.getBalance(this.$store.getters.getMetamaskWallet.metaMaskAddress, (err, response2) => {
                  if (!err){
                        var newbalance = Web3.utils.fromWei(response2, "ether");
                        this.yourWalletBalanceEth = newbalance;
                        this.$store.dispatch('createMetamaskIntentTokenBuy', {email: this.user.email, 
                                                                              tokens: parseInt(this.cryptoSlider.val), 
                                                                              price:  this.cryptoDashboardData.release_price, 
                                                                              amount: parseFloat((parseInt(this.cryptoSlider.val) * parseFloat(this.cryptoDashboardData.release_price))).toFixed(2) * res}
                        ).then( response4 => {
                            this.intentMetamaskID = response4.data.orderID;
                            this.cryptoPayModel = 4;
                        }).catch( error2 => {
                          if (!error2.response){
                              this.notifyError("Error occured.. Please try again later.");
                          } else {
                              this.initializeError = true;
                          }
                        });   
                  } else {
                    this.initializeError = true;
                  }
            })
          }).catch(error => {
            this.notifyError("Error occured.. Please try again later.");
          })
        } else {
          this.initializeError = true;
        }
      }
    },
    async submitPaymentStripe() {
      this.initializeError = false;
      this.executeStripe = false;
      this.executeStripe = true;
      try{
        let elements = await this.$refs.paymentRefStripe.submit();
        this.executeStripe = false;
      } catch(error){
        this.executeStripe = false;
        if (error.type === "card_error" || error.type === "validation_error") {
          this.initializeError2 = true;
        } else {
          this.initializeError2 = true;
        }
      }
    },
    async GetETHExchangeRate() {
      var requestOptions = { method: "GET", redirect: "follow" };
      return fetch("https://api.coinbase.com/v2/exchange-rates?currency=USD", requestOptions)
        .then((response) => response.json())
        .then((result) => {return(result.data.rates.ETH)})
        .catch((error) => {return("error", error)});
    },
    sleeper(ms) {
        return new Promise((resolve) => {
          setTimeout(resolve, ms);
        });
      },
    async sleeper2(){
        await this.sleeper(1000);
    },
    checkScreenSize() {
      if(window.innerWidth > 768){
        this.navbar = false;
      } else {
        this.navbar = true;
      }
    },
    handleClickMenu(link){
      if (link === 'Terms'){
        window.open('https://muzible.com/terms','_blank');
      } else if (link === 'Privacy'){
        window.open('https://muzible.com/privacy','_blank');
      } else if (link === 'TryMuzE'){
        window.location.href = 'https://muze.muzible.com/';
      } else if (link === 'TryVerse'){
        window.location.href = 'https://verse.muzible.com/';
      } else {
        if (this.$route.name !== link ) {
          this.$router.push({ name: link });
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
@use './css/styles.scss';

/* Payment Header */
.payment-header {
  margin-bottom: 24px;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 16px;
  margin-bottom: 12px;
}

.header-icon {
  font-size: 48px !important;
  color: white;
}

.header-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 42px;
  font-weight: 700;
  color: white;
  margin: 0;
}

.header-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin: 0 0 20px 0;
  line-height: 1.5;
}

.header-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  transition: all 0.2s;
}

.action-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

/* Info Alert */
.payment-info-alert {
  background: rgba(33, 150, 243, 0.1) !important;
  border: 1px solid rgba(33, 150, 243, 0.4) !important;
  border-radius: 12px !important;
  padding: 16px !important;

  :deep(.v-alert__content) {
    color: white !important;
  }
}

.alert-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.alert-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 18px;
  font-weight: 700;
  color: white;
  letter-spacing: 0.5px;
}

.alert-text {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

/* Payment Section */
.payment-section {
  margin-bottom: 40px;
}

/* Payment Card */
.payment-card {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.95), rgba(18, 18, 18, 0.95));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  overflow: hidden;
  transition: all 0.3s ease;
  animation: slideIn 0.4s ease forwards;
}

.payment-step {
  padding: 32px 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
}

.payment-step:last-child {
  border-bottom: none;
}

.step-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 24px;
  font-weight: 600;
  color: white;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.subsection-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: white;
  margin: 24px 0 16px 0;
  display: flex;
  align-items: center;
  gap: 8px;
}

/* Step Alert */
.step-alert {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  font-weight: 600;
  margin-bottom: 16px;
  border-radius: 8px;
  padding: 12px 16px;
  background: rgba(226, 71, 75, 0.08);
  color: #e2474b;
  border: 1px solid #e2474b;
}

/* Token Selection */
.token-selection {
  margin-bottom: 24px;
}

.token-info {
  display: flex;
  justify-content: space-between;
  margin-bottom: 32px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.token-amount,
.token-price {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.token-amount .label,
.token-price .label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.6);
  font-family: "Darker Grotesque", sans-serif;
}

.token-amount .value,
.token-price .value {
  font-size: 28px;
  font-weight: 700;
  color: white;
  font-family: "Darker Grotesque", sans-serif;
}

.slider-wrapper {
  padding: 0 16px;
}

:deep(.v-slider .v-slider-thumb__label) {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 14px !important;
  font-weight: 600 !important;
  color: white !important;
  background: rgba(0, 0, 0, 0.8) !important;
  border-radius: 6px !important;
}

:deep(.v-slider .v-slider-thumb) {
  border: 0px solid white !important;
}

:deep(.v-slider .v-slider-track__fill) {
  background: white !important;
}

/* Terms Checkbox */
.terms-checkbox {
  margin-bottom: 24px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
}

.checkbox-label {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

.policy-link {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  color: white !important;
  text-decoration: underline;
  padding: 0 4px !important;
  min-width: auto !important;
  height: auto !important;
}

/* Payment Summary */
.payment-summary {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.summary-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
}

.summary-row:last-child {
  border-bottom: none;
}

.summary-row.total {
  padding-top: 16px;
  margin-top: 8px;
  border-top: 2px solid rgba(255, 255, 255, 0.2);
}

.summary-label {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
}

.summary-row.total .summary-label,
.summary-row.total .summary-value {
  font-size: 20px;
  font-weight: 700;
  color: white;
}

.summary-value {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: white;
}

/* Payment Methods */
.payment-methods {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 16px;
  margin-bottom: 24px;
}

@media (max-width: 600px) {
  .payment-methods {
    grid-template-columns: 1fr;
  }
}

.payment-method {
  padding: 20px;
  background: white;
  border: 2px solid transparent;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
}

.payment-method:hover {
  border-color: #2196f3;
  background: linear-gradient(90deg, rgba(33,150,243,0.12) 0%, rgba(33,150,243,0.08) 100%);
  transform: translateY(-3px) scale(1.03);
  box-shadow: 0 4px 16px rgba(33,150,243,0.18);
  transition: all 0.2s;
}

.payment-method.selected {
  border-color: #2196f3;
  background: linear-gradient(90deg, rgba(33,150,243,0.18) 0%, rgba(33,150,243,0.10) 100%);
  box-shadow: 0 0 0 3px rgba(33,150,243,0.18);
  transform: scale(1.04);
}

/* Payment Gateway */
.payment-gateway {
  margin-top: 16px;
}

.gateway-logo {
  display: flex;
  justify-content: center;
  margin-bottom: 24px;
  padding: 20px;
  background: white;
  border-radius: 8px;
}

.stripe-form {
  margin-bottom: 24px;
}

.paypal-container {
  margin-bottom: 24px;
  min-height: 150px;
}

/* Metamask Info */
.metamask-info {
  background: rgba(255, 255, 255, 0.05);
  border-radius: 8px;
  padding: 20px;
  margin-bottom: 24px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 12px 0;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row.total {
  padding-top: 16px;
  margin-top: 8px;
  border-top: 2px solid rgba(255, 255, 255, 0.2);
}

.info-label {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
}

.info-row.total .info-label,
.info-row.total .info-value {
  font-size: 20px;
  font-weight: 700;
  color: white;
}

.info-value {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  font-weight: 600;
  color: white;
}

/* Error Message */
.error-message {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  margin-bottom: 16px;
  background: rgba(226, 71, 75, 0.08);
  color: #e2474b;
  border: 1px solid #e2474b;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
}

/* Step Actions */
.step-actions {
  margin-top: 24px;
}

/* Empty State */
.payment-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 60px 20px;
  text-align: center;
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.5), rgba(18, 18, 18, 0.5));
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
}

.empty-icon-wrapper {
  margin-bottom: 24px;
  animation: float 3s ease-in-out infinite;
}

@keyframes float {
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
}

.empty-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 32px;
  font-weight: 700;
  color: white;
  margin-bottom: 12px;
}

.empty-subtitle {
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 32px;
  max-width: 500px;
  line-height: 1.6;
}

.empty-actions {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
  justify-content: center;
}

.empty-action-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 16px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
  padding: 0 24px !important;
}

.empty-action-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.3);
}

/* Success Dialog */
.success-dialog-card {
  background: linear-gradient(145deg, rgba(26, 26, 26, 0.98), rgba(18, 18, 18, 0.98));
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: 16px;
}

.success-icon-wrapper {
  margin-bottom: 16px;
}

.success-title {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 28px;
  font-weight: 700;
  color: white;
  margin-bottom: 12px;
}

.success-subtitle {
  font-family: "Darker Grotesque", sans-serif;
  font-size: 16px;
  color: rgba(255, 255, 255, 0.7);
  line-height: 1.5;
  margin: 0;
}

.dialog-btn {
  font-family: "Darker Grotesque", sans-serif !important;
  font-size: 15px !important;
  font-weight: 600 !important;
  text-transform: none !important;
  border-color: rgba(255, 255, 255, 0.3) !important;
}

.dialog-btn:hover {
  background: rgba(255, 255, 255, 0.1) !important;
  border-color: rgba(255, 255, 255, 0.5) !important;
}

/* Animations */
@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.payment-card,
.payment-empty-state,
.payment-info-alert {
  animation: slideIn 0.4s ease forwards;
}

/* Responsive */
@media (max-width: 768px) {
  .header-title {
    font-size: 32px;
  }

  .payment-step {
    padding: 24px 16px;
  }

  .header-actions {
    width: 100%;
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
  }

  .token-info {
    flex-direction: column;
    gap: 16px;
  }
}

/* Override Vuetify */
:deep(.v-btn:hover) > .v-btn__overlay {
  --v-hover-opacity: 0.1;
  opacity: 0.1;
}

:deep(.v-list-item:hover) > .v-list-item__overlay {
  --v-hover-opacity: 0.1;
  opacity: 0.1;
}
</style>